import { Plus, X } from 'lucide-react';
import { useId, useState } from 'react';

import { Button, Field, Input } from '@platform/ui';

import { tagProblem, CRM_TAGS_PER_EVENT } from './crm';

/**
 * The tag chips for one platform event (M3.12, FR-CRM-06).
 *
 * Not `TagInput` from `@platform/ui`: that one picks from a known list, and a CRM tag is
 * the clinic's own word. What it needs instead is free text under a grammar, plus the one
 * thing a generic chip control has no place for — a **message on a single chip**, because
 * the API refuses one tag at a time and names its index (`['tags', <event>, <index>]`).
 * A refusal shown above the list would leave the admin guessing which of eight tags it
 * meant.
 *
 * A text box and real buttons, so there is no ARIA to get wrong: Enter adds, each chip's
 * remove button carries its own name, and a refused chip is outlined *and* carries its
 * message in text (rules §48.2, §48.5).
 */

export interface CrmTagEditorProps {
  label: string;
  help: string;
  tags: string[];
  onChange: (next: string[]) => void;
  /** Server messages by chip index, from `crmIssuesToErrors`. */
  errors?: Record<number, string>;
  disabled?: boolean;
}

export function CrmTagEditor({
  label,
  help,
  tags,
  onChange,
  errors = {},
  disabled = false,
}: CrmTagEditorProps) {
  const inputId = useId();
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState<string | undefined>(undefined);

  const full = tags.length >= CRM_TAGS_PER_EVENT;

  const add = (): void => {
    const tag = draft.trim();
    const why = tagProblem(tag, tags);
    if (why !== undefined) {
      setProblem(why);
      return;
    }
    onChange([...tags, tag]);
    setDraft('');
    setProblem(undefined);
  };

  const remove = (index: number): void => {
    onChange(tags.filter((_, position) => position !== index));
    setProblem(undefined);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-end gap-2">
        <Field
          id={inputId}
          label={label}
          help={full ? `That is the limit of ${String(CRM_TAGS_PER_EVENT)} tags.` : help}
          {...(problem === undefined ? {} : { error: problem })}
          className="min-w-[14rem] grow max-[560px]:w-full"
        >
          <Input
            value={draft}
            disabled={disabled || full}
            spellCheck={false}
            autoComplete="off"
            placeholder="recall:spring"
            maxLength={60}
            onChange={(event) => {
              setDraft(event.currentTarget.value);
              setProblem(undefined);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              /* The card is inside a <form>; Enter here means "add this tag", never "save
                 the whole mapping". */
              event.preventDefault();
              add();
            }}
          />
        </Field>
        <Button
          variant="secondary"
          disabled={disabled || full}
          onClick={add}
          icon={<Plus size={16} strokeWidth={2} aria-hidden={true} />}
        >
          Add
          <span className="sr-only"> a tag to {label}</span>
        </Button>
      </div>

      <ul aria-label={`${label} tags`} className="flex flex-wrap items-center gap-2">
        {tags.length === 0 ? (
          <li className="text-base text-neutral-400-text">No tags</li>
        ) : (
          tags.map((tag, index) => {
            const error = errors[index];
            return (
              <li key={`${tag}-${String(index)}`} className="flex flex-col">
                <span
                  className={
                    error === undefined
                      ? 'inline-flex items-center gap-1 rounded-full bg-neutral-100 py-[2px] pr-[4px] pl-[10px] font-mono text-2xs font-semibold text-neutral-600'
                      : 'inline-flex items-center gap-1 rounded-full bg-danger-subtle py-[2px] pr-[4px] pl-[10px] font-mono text-2xs font-semibold text-danger-ink ring-1 ring-danger-ink'
                  }
                >
                  {tag}
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      remove(index);
                    }}
                    aria-label={`Remove ${tag} from ${label}`}
                    className="inline-flex size-6 cursor-pointer items-center justify-center rounded-full text-current hover:bg-surface focus:shadow-focus focus:outline-none disabled:cursor-not-allowed"
                  >
                    <X size={12} strokeWidth={2.5} aria-hidden="true" />
                  </button>
                </span>
                {error === undefined ? null : (
                  <p role="alert" className="mt-[3px] max-w-[22rem] text-sm text-danger-ink">
                    <span aria-hidden="true">&#9888; </span>
                    {error}
                  </p>
                )}
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
